/**
 * Coordinate utilities for MapLibre GL JS.
 * MapLibre uses [lng, lat] order; many internal variables use {lat, lng} objects.
 */

var DEG2RAD = Math.PI / 180;
var EARTH_RADIUS = 6371000; // meters

/**
 * Convert (lat, lng) or [lat, lng] or {lat, lng} to [lng, lat] for MapLibre.
 */
export function toLngLat(latOrObj, lng) {
    if (Array.isArray(latOrObj)) return [latOrObj[1], latOrObj[0]];
    if (latOrObj && typeof latOrObj === 'object' && 'lat' in latOrObj) return [latOrObj.lng, latOrObj.lat];
    return [lng, latOrObj];
}

/**
 * Convert [lng, lat] or maplibregl.LngLat to {lat, lng} plain object.
 */
export function toLatLng(lngLatOrArr) {
    if (Array.isArray(lngLatOrArr)) return { lat: lngLatOrArr[1], lng: lngLatOrArr[0] };
    if (lngLatOrArr && typeof lngLatOrArr === 'object') return { lat: lngLatOrArr.lat, lng: lngLatOrArr.lng };
    return null;
}

/**
 * Midpoint between two {lat, lng} objects. Returns {lat, lng}.
 */
export function midpoint(a, b) {
    if (!a || !b) return null;
    return { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
}

function pointInRing(point, ring) {
    if (!Array.isArray(point) || !Array.isArray(ring) || ring.length < 3) return false;
    var inside = false;
    for (var index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
        var a = ring[index];
        var b = ring[previous];
        if (!Array.isArray(a) || !Array.isArray(b)) continue;
        var crosses = ((a[1] > point[1]) !== (b[1] > point[1]))
            && (point[0] < (b[0] - a[0]) * (point[1] - a[1]) / ((b[1] - a[1]) || 0.000000000001) + a[0]);
        if (crosses) inside = !inside;
    }
    return inside;
}

function pointInPolygonCoordinates(point, rings) {
    if (!Array.isArray(rings) || !rings.length || !pointInRing(point, rings[0])) return false;
    for (var index = 1; index < rings.length; index += 1) {
        if (pointInRing(point, rings[index])) return false;
    }
    return true;
}

/** Return true when [lng, lat] lies inside a Polygon or MultiPolygon geometry. */
export function pointInPolygonGeometry(point, geometry) {
    if (!geometry || !Array.isArray(point)) return false;
    if (geometry.type === 'Polygon') {
        return pointInPolygonCoordinates(point, geometry.coordinates);
    }
    if (geometry.type === 'MultiPolygon') {
        return (geometry.coordinates || []).some(function(polygon) {
            return pointInPolygonCoordinates(point, polygon);
        });
    }
    return false;
}

/**
 * Haversine distance in meters between two {lat, lng} objects.
 */
export function haversineDistance(a, b) {
    if (!a || !b) return 0;
    var dLat = (b.lat - a.lat) * DEG2RAD;
    var dLng = (b.lng - a.lng) * DEG2RAD;
    var sinLat = Math.sin(dLat / 2);
    var sinLng = Math.sin(dLng / 2);
    var h = sinLat * sinLat + Math.cos(a.lat * DEG2RAD) * Math.cos(b.lat * DEG2RAD) * sinLng * sinLng;
    return 2 * EARTH_RADIUS * Math.asin(Math.sqrt(h));
}

/**
 * Pad a MapLibre LngLatBounds by a factor (e.g. 0.08 = 8% on each side).
 * Returns a new LngLatBounds.
 */
export function padBounds(bounds, factor) {
    var sw = bounds.getSouthWest();
    var ne = bounds.getNorthEast();
    var latPad = (ne.lat - sw.lat) * factor;
    var lngPad = (ne.lng - sw.lng) * factor;
    return new maplibregl.LngLatBounds(
        [sw.lng - lngPad, sw.lat - latPad],
        [ne.lng + lngPad, ne.lat + latPad]
    );
}

/**
 * Build a bbox string "west,south,east,north" from a MapLibre LngLatBounds,
 * with optional padding factor and decimal precision.
 */
export function bboxString(bounds, padding, decimals) {
    var b = padding ? padBounds(bounds, padding) : bounds;
    var d = decimals != null ? decimals : 4;
    var sw = b.getSouthWest();
    var ne = b.getNorthEast();
    return [sw.lng.toFixed(d), sw.lat.toFixed(d), ne.lng.toFixed(d), ne.lat.toFixed(d)].join(',');
}

/**
 * Find the nearest point on a GeoJSON geometry to a reference {lat, lng}.
 * Returns [lng, lat] or null. For Point geometries returns the point itself.
 * For Polygon/MultiPolygon returns the nearest point on any boundary ring.
 * Uses flat-earth projection (cosine correction) — accurate enough for distances under ~50 km.
 */
export function nearestPointOnGeometry(geom, ref) {
    if (!geom || !ref) return null;

    if (geom.type === 'Point') return geom.coordinates;

    if (geom.type === 'MultiPoint') {
        var bestMultiPoint = null;
        var bestMultiPointD = Infinity;
        for (var mp = 0; mp < geom.coordinates.length; mp++) {
            var multiPoint = geom.coordinates[mp];
            if (!Array.isArray(multiPoint) || multiPoint.length < 2) continue;
            var multiPointD = _flatDistSq(multiPoint[0], multiPoint[1], ref.lng, ref.lat, ref.lat);
            if (multiPointD < bestMultiPointD) {
                bestMultiPointD = multiPointD;
                bestMultiPoint = multiPoint;
            }
        }
        return bestMultiPoint;
    }

    if (geom.type === 'LineString') {
        return _nearestOnLine(geom.coordinates, ref);
    }

    if (geom.type === 'MultiLineString') {
        var best = null;
        var bestD = Infinity;
        for (var i = 0; i < geom.coordinates.length; i++) {
            var p = _nearestOnLine(geom.coordinates[i], ref);
            if (p) {
                var d = _flatDistSq(p[0], p[1], ref.lng, ref.lat, ref.lat);
                if (d < bestD) { bestD = d; best = p; }
            }
        }
        return best;
    }

    if (geom.type === 'Polygon') {
        return _nearestOnRings(geom.coordinates, ref);
    }

    if (geom.type === 'MultiPolygon') {
        var bestMp = null;
        var bestMpD = Infinity;
        for (var k = 0; k < geom.coordinates.length; k++) {
            var p2 = _nearestOnRings(geom.coordinates[k], ref);
            if (p2) {
                var d2 = _flatDistSq(p2[0], p2[1], ref.lng, ref.lat, ref.lat);
                if (d2 < bestMpD) { bestMpD = d2; bestMp = p2; }
            }
        }
        return bestMp;
    }

    if (geom.type === 'GeometryCollection') {
        var bestCollection = null;
        var bestCollectionD = Infinity;
        var geometries = Array.isArray(geom.geometries) ? geom.geometries : [];
        for (var gc = 0; gc < geometries.length; gc++) {
            var collectionPoint = nearestPointOnGeometry(geometries[gc], ref);
            if (!collectionPoint) continue;
            var collectionD = _flatDistSq(collectionPoint[0], collectionPoint[1], ref.lng, ref.lat, ref.lat);
            if (collectionD < bestCollectionD) {
                bestCollectionD = collectionD;
                bestCollection = collectionPoint;
            }
        }
        return bestCollection;
    }

    return centroidOfGeometry(geom);
}

function _nearestOnRings(rings, ref) {
    if (!rings || !rings.length) return null;
    var best = null;
    var bestD = Infinity;
    for (var i = 0; i < rings.length; i++) {
        var ring = rings[i];
        if (!ring || !ring.length) continue;
        var first = ring[0];
        var last = ring[ring.length - 1];
        var isClosed = first && last && first[0] === last[0] && first[1] === last[1];
        var p = _nearestOnLine(isClosed ? ring : ring.concat([first]), ref);
        if (p) {
            var d = _flatDistSq(p[0], p[1], ref.lng, ref.lat, ref.lat);
            if (d < bestD) { bestD = d; best = p; }
        }
    }
    return best;
}

function _nearestOnLine(coords, ref) {
    if (!coords || coords.length === 0) return null;
    if (coords.length === 1) return coords[0];

    var cosLat = Math.cos(ref.lat * DEG2RAD);
    var rx = ref.lng;
    var ry = ref.lat;
    var bestX = coords[0][0];
    var bestY = coords[0][1];
    var bestD = _flatDistSqRaw(rx, ry, bestX, bestY, cosLat);

    for (var i = 0; i < coords.length - 1; i++) {
        var ax = coords[i][0], ay = coords[i][1];
        var bx = coords[i + 1][0], by = coords[i + 1][1];
        var dx = (bx - ax) * cosLat;
        var dy = by - ay;
        var lenSq = dx * dx + dy * dy;
        var t = lenSq > 0 ? (((rx - ax) * cosLat * dx + (ry - ay) * dy) / lenSq) : 0;
        if (t < 0) t = 0;
        else if (t > 1) t = 1;
        var px = ax + t * (bx - ax);
        var py = ay + t * (by - ay);
        var d = _flatDistSqRaw(rx, ry, px, py, cosLat);
        if (d < bestD) { bestD = d; bestX = px; bestY = py; }
    }

    return [bestX, bestY];
}

function _flatDistSq(lng1, lat1, lng2, lat2, refLat) {
    var cosLat = Math.cos(refLat * DEG2RAD);
    return _flatDistSqRaw(lng1, lat1, lng2, lat2, cosLat);
}

function _flatDistSqRaw(lng1, lat1, lng2, lat2, cosLat) {
    var dx = (lng2 - lng1) * cosLat;
    var dy = lat2 - lat1;
    return dx * dx + dy * dy;
}

/**
 * Compute centroid of a GeoJSON geometry. Returns [lng, lat].
 */
export function centroidOfGeometry(geom) {
    if (!geom) return null;

    if (geom.type === 'Point') {
        return geom.coordinates;
    }

    if (geom.type === 'MultiPoint' || geom.type === 'LineString') {
        var coords = geom.coordinates;
        if (!coords.length) return null;
        var sLng = 0, sLat = 0;
        for (var i = 0; i < coords.length; i++) {
            sLng += coords[i][0];
            sLat += coords[i][1];
        }
        return [sLng / coords.length, sLat / coords.length];
    }

    if (geom.type === 'Polygon' || geom.type === 'MultiLineString') {
        var ring = geom.coordinates[0];
        if (!ring || !ring.length) return null;
        var sLng2 = 0, sLat2 = 0;
        for (var j = 0; j < ring.length; j++) {
            sLng2 += ring[j][0];
            sLat2 += ring[j][1];
        }
        return [sLng2 / ring.length, sLat2 / ring.length];
    }

    if (geom.type === 'MultiPolygon') {
        var allCoords = [];
        for (var k = 0; k < geom.coordinates.length; k++) {
            var polyRing = geom.coordinates[k][0];
            if (polyRing) {
                for (var m = 0; m < polyRing.length; m++) {
                    allCoords.push(polyRing[m]);
                }
            }
        }
        if (!allCoords.length) return null;
        var sLng3 = 0, sLat3 = 0;
        for (var n = 0; n < allCoords.length; n++) {
            sLng3 += allCoords[n][0];
            sLat3 += allCoords[n][1];
        }
        return [sLng3 / allCoords.length, sLat3 / allCoords.length];
    }

    if (geom.type === 'GeometryCollection') {
        var centers = (Array.isArray(geom.geometries) ? geom.geometries : [])
            .map(function(child) { return centroidOfGeometry(child); })
            .filter(function(center) { return center !== null; });
        if (!centers.length) return null;
        var collectionLng = 0;
        var collectionLat = 0;
        for (var gc = 0; gc < centers.length; gc++) {
            collectionLng += centers[gc][0];
            collectionLat += centers[gc][1];
        }
        return [collectionLng / centers.length, collectionLat / centers.length];
    }

    return null;
}
